export interface ExperienceDate {
  year?: number | null;
  month?: number | null;
  day?: number | null;
}

export interface Experience {
  title?: string | null;
  company?: string | null;
  starts_at?: ExperienceDate | null;
  ends_at?: ExperienceDate | null;
  is_current?: boolean | null;
  is_default?: boolean | null;
  description?: string | null;
  location?: string | null;
  logo_url?: string | null;
  company_linkedin_profile_url?: string | null;
  company_website_domain?: string | null;
}

const datePart = (raw: unknown): ExperienceDate | null => {
  if (raw && typeof raw === 'object') return raw as ExperienceDate;
  if (typeof raw === 'number') return { year: raw };
  if (typeof raw !== 'string' || !/^\d{4}(?:-\d{2})?(?:-\d{2})?/.test(raw))
    return null;
  const [year, month, day] = raw.slice(0, 10).split('-').map(Number);
  return { year, month: month || null, day: day || null };
};

export function isCurrentExperience(item: object, now = new Date()): boolean {
  const role = item as Experience & { end_date?: unknown };
  const end = datePart(role.ends_at ?? role.end_date);
  if (end?.year) {
    const last = new Date(end.year, end.month ?? 12, 0).getDate();
    const ended = new Date(end.year, (end.month ?? 12) - 1, end.day ?? last);
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    if (ended < today) return false;
  }
  if (typeof role.is_current === 'boolean') return role.is_current;
  return !(role.ends_at ?? role.end_date);
}

export function normalizeExperience(item: object): Experience {
  const raw = item as Record<string, unknown>;
  return {
    ...raw,
    title: (raw.title || raw.employee_title) as string | null,
    company: (raw.company || raw.employer_name || raw.name) as string | null,
    starts_at: datePart(raw.starts_at || raw.start_date),
    ends_at: datePart(raw.ends_at || raw.end_date),
    is_current: isCurrentExperience(item),
    is_default: (raw.is_default ?? raw.employer_is_default) as boolean | null,
    logo_url: (raw.logo_url ||
      raw.company_logo_url ||
      raw.employer_logo_url) as string | null,
    company_linkedin_profile_url: (raw.company_linkedin_profile_url ||
      raw.company_linkedin_url) as string | null,
    description: (raw.description || raw.employee_description) as string | null,
    location: (raw.location || raw.employee_location) as string | null,
    company_website_domain: (raw.company_website_domain ||
      raw.employer_company_website_domain) as string | null,
  };
}

export function normalizeExperiences<T extends object>(
  items: T[]
): Array<T & Experience>;
export function normalizeExperiences(items: unknown): Experience[];
export function normalizeExperiences(items: unknown): Experience[] {
  if (!Array.isArray(items)) return [];
  return items
    .filter((item) => item && typeof item === 'object' && !Array.isArray(item))
    .map(normalizeExperience)
    .sort((a, b) => {
      const priority = (role: Experience) =>
        role.is_current ? (role.is_default ? 0 : 1) : 2;
      return priority(a) - priority(b);
    });
}

export const primaryExperience = (items: unknown): Experience | undefined =>
  normalizeExperiences(items)[0];

export function experienceDateRange(item: object): string {
  const role = normalizeExperience(item);
  const format = (part?: ExperienceDate | null) =>
    part?.year
      ? part.month
        ? `${new Date(2000, part.month - 1, 1).toLocaleString('en-US', { month: 'short' })} ${part.year}`
        : String(part.year)
      : '';
  const from = format(role.starts_at);
  const to = format(role.ends_at) || (role.is_current ? 'Present' : 'Past');
  return from
    ? `${from} - ${to}`
    : role.ends_at
      ? `Until ${to}`
      : role.is_current
        ? ''
        : 'Past';
}
